import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Folder, Trash2, Download, Plus, RefreshCw, X, Gavel, CheckCircle2, XCircle, AlertCircle, Pencil, Check, ChevronUp, ChevronDown } from 'lucide-react';
import { SubmissionFolder, MoveSubmission, SubmissionJudgeScore, SubmissionJudgeStatus, User } from '../types';
import { useI18n } from '../i18n';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';

interface AdminSubmissionsProps {
  user: User;
}

const parseSubmissionTrickScore = (trickName: string) => {
  const match = String(trickName || '').match(/^(\d+(?:\.\d+)?)-/);
  return match ? parseFloat(match[1]) : 0;
};

const getDefaultJudgeStatuses = (count: number) => Array(count).fill('pending') as SubmissionJudgeStatus[];

const getJudgeScoreBreakdown = (tricks: string[], statuses: SubmissionJudgeStatus[], initialScore: number = 0) => {
  let baseSum = 0;
  let bonus = 0;
  let rawSum = 0;
  let allSuccess = tricks.length > 0;

  tricks.forEach((trick, index) => {
    const trickScore = parseSubmissionTrickScore(trick);
    rawSum += trickScore;
    if (statuses[index] === 'success') {
      baseSum += Math.pow(trickScore, 2);
      if (trickScore === 11) bonus += 30;
      if (trickScore === 12) bonus += 50;
    } else {
      allSuccess = false;
    }
  });

  const allSuccessBonus = allSuccess ? rawSum : 0;

  return {
    initialScore,
    baseSum,
    bonus,
    allSuccessBonus,
    totalScore: initialScore + baseSum + bonus + allSuccessBonus
  };
};

const calculateJudgeScore = (tricks: string[], statuses: SubmissionJudgeStatus[], initialScore: number = 0) =>
  getJudgeScoreBreakdown(tricks, statuses, initialScore).totalScore;

const getStatusCount = (statuses: SubmissionJudgeStatus[], target: SubmissionJudgeStatus) => statuses.filter(status => status === target).length;
const areJudgeStatusesEqual = (left: SubmissionJudgeStatus[], right: SubmissionJudgeStatus[]) =>
  left.length === right.length && left.every((status, index) => status === right[index]);

const hasSubmissionDoubt = (submission: MoveSubmission) => (submission.judgeScores || []).some(
  score => (score.trickStatuses || []).some(status => status === 'doubt')
);

const formatScoreValue = (score: number) => (Number.isInteger(score) ? String(score) : score.toFixed(1));
const getSubmissionCompetitionNumber = (submission: MoveSubmission) => {
  const value = typeof submission.competitionNumber === 'string' ? submission.competitionNumber.trim() : '';
  return value || '--';
};

const getFinalSubmissionScore = (submission: MoveSubmission) => {
  const normalizedFirstJudgeScore = getNormalizedSubmissionJudgeScore(submission, (submission.judgeScores || [])[0]);
  return typeof normalizedFirstJudgeScore?.totalScore === 'number' ? normalizedFirstJudgeScore.totalScore : null;
};

const getFinalSubmissionInitialScore = (submission: MoveSubmission) => {
  const normalizedFirstJudgeScore = getNormalizedSubmissionJudgeScore(submission, (submission.judgeScores || [])[0]);
  if (typeof normalizedFirstJudgeScore?.initialScore === 'number') {
    return normalizedFirstJudgeScore.initialScore;
  }
  return getSubmissionInitialScore(submission);
};

const getDisplayedSubmissionInitialScore = (submission: MoveSubmission, viewerJudgeId?: string | null) => {
  const viewerJudgeScore = viewerJudgeId
    ? getNormalizedSubmissionJudgeScore(
      submission,
      (submission.judgeScores || []).find(score => score.judgeId === viewerJudgeId)
    )
    : null;
  return viewerJudgeScore?.initialScore ?? getFinalSubmissionInitialScore(submission) ?? getSubmissionInitialScore(submission);
};

const getFinalSubmissionScoreBreakdown = (submission: MoveSubmission) => {
  const normalizedFirstJudgeScore = getNormalizedSubmissionJudgeScore(submission, (submission.judgeScores || [])[0]);
  if (!normalizedFirstJudgeScore) {
    return null;
  }

  return getJudgeScoreBreakdown(
    submission.tricks,
    normalizedFirstJudgeScore.trickStatuses as SubmissionJudgeStatus[],
    typeof normalizedFirstJudgeScore.initialScore === 'number' ? normalizedFirstJudgeScore.initialScore : 0
  );
};

const getSubmissionInitialScore = (submission: MoveSubmission) =>
  typeof submission.initialScore === 'number' && Number.isFinite(submission.initialScore)
    ? submission.initialScore
    : 0;

const getNormalizedSubmissionJudgeScore = (
  submission: MoveSubmission,
  judgeScore?: SubmissionJudgeScore | null
): SubmissionJudgeScore | null => {
  if (!judgeScore) {
    return null;
  }

  const normalizedStatuses = Array.isArray(judgeScore.trickStatuses)
    ? [...judgeScore.trickStatuses]
    : getDefaultJudgeStatuses(submission.tricks.length);
  while (normalizedStatuses.length < submission.tricks.length) normalizedStatuses.push('pending');

  const normalizedInitialScore =
    typeof judgeScore.initialScore === 'number' && Number.isFinite(judgeScore.initialScore)
      ? judgeScore.initialScore
      : getSubmissionInitialScore(submission);

  return {
    ...judgeScore,
    trickStatuses: normalizedStatuses as SubmissionJudgeStatus[],
    initialScore: normalizedInitialScore,
    totalScore: calculateJudgeScore(
      submission.tricks,
      normalizedStatuses as SubmissionJudgeStatus[],
      normalizedInitialScore
    )
  };
};

const shouldUseSubmissionInitialScore = (
  submissionInitialScore: number,
  existingInitialScore: number | null | undefined,
  statuses: SubmissionJudgeStatus[] = [],
  totalScore?: number
) => {
  if (!submissionInitialScore) return false;
  const normalizedExisting = typeof existingInitialScore === 'number' && Number.isFinite(existingInitialScore)
    ? existingInitialScore
    : null;
  const isUntouched = statuses.every(status => status === 'pending') && (!totalScore || totalScore === normalizedExisting || totalScore === 0);
  return (normalizedExisting === null || normalizedExisting === 0) && isUntouched;
};

const getSubmissionRevisionValue = (submission?: MoveSubmission | null) =>
  typeof submission?.revision === 'number' && Number.isFinite(submission.revision) ? submission.revision : 0;

const getSubmissionUpdatedAtValue = (submission?: MoveSubmission | null) =>
  typeof submission?.updatedAt === 'number' && Number.isFinite(submission.updatedAt) ? submission.updatedAt : 0;

const ADMIN_SUBMISSION_JUDGE_DRAFT_STORAGE_PREFIX = 'kendama-admin-submission-judge-drafts';

type AdminSubmissionJudgeDraft = {
  initialScoreInput: string;
  trickStatuses: SubmissionJudgeStatus[];
};

const getAdminSubmissionJudgeDraftStorageKey = (userId: string) => (
  `${ADMIN_SUBMISSION_JUDGE_DRAFT_STORAGE_PREFIX}:${userId}`
);

const sanitizeAdminSubmissionJudgeDraft = (value: unknown): AdminSubmissionJudgeDraft | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<AdminSubmissionJudgeDraft>;
  const initialScoreInput = typeof candidate.initialScoreInput === 'string'
    ? candidate.initialScoreInput
    : '0';
  const trickStatuses = Array.isArray(candidate.trickStatuses)
    ? candidate.trickStatuses.filter((status): status is SubmissionJudgeStatus => (
      status === 'pending' || status === 'success' || status === 'loss' || status === 'doubt'
    ))
    : [];
  return {
    initialScoreInput,
    trickStatuses
  };
};

const readAdminSubmissionJudgeDrafts = (userId: string): Record<string, AdminSubmissionJudgeDraft> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(getAdminSubmissionJudgeDraftStorageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.entries(parsed).reduce<Record<string, AdminSubmissionJudgeDraft>>((acc, [submissionId, draft]) => {
      const sanitized = sanitizeAdminSubmissionJudgeDraft(draft);
      if (sanitized) {
        acc[submissionId] = sanitized;
      }
      return acc;
    }, {});
  } catch (error) {
    console.warn('Failed to restore admin submission judge drafts:', error);
    return {};
  }
};

const writeAdminSubmissionJudgeDrafts = (userId: string, drafts: Record<string, AdminSubmissionJudgeDraft>) => {
  if (typeof window === 'undefined') return;
  const storageKey = getAdminSubmissionJudgeDraftStorageKey(userId);
  const entries = Object.entries(drafts).filter(([, draft]) => (
    typeof draft.initialScoreInput === 'string' && Array.isArray(draft.trickStatuses)
  ));
  if (entries.length === 0) {
    window.localStorage.removeItem(storageKey);
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(entries)));
};

const clearAdminSubmissionJudgeDraft = (userId: string, submissionId: string) => {
  if (typeof window === 'undefined') return;
  const allDrafts = readAdminSubmissionJudgeDrafts(userId);
  if (!allDrafts[submissionId]) return;
  delete allDrafts[submissionId];
  writeAdminSubmissionJudgeDrafts(userId, allDrafts);
};

const mergeSubmissionByRevision = (current: MoveSubmission | undefined, incoming: MoveSubmission | undefined) => {
  if (!incoming) return current;
  if (!current) return incoming;

  const currentRevision = getSubmissionRevisionValue(current);
  const incomingRevision = getSubmissionRevisionValue(incoming);

  if (incomingRevision > currentRevision) return incoming;
  if (incomingRevision < currentRevision) return current;

  return getSubmissionUpdatedAtValue(incoming) >= getSubmissionUpdatedAtValue(current) ? incoming : current;
};

const areStringArraysEqual = (left: string[], right: string[]) => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

const moveSubmissionIdByOffset = (
  orderedIds: string[],
  submissionId: string,
  offset: -1 | 1
) => {
  const next = [...orderedIds];
  const currentIndex = next.indexOf(submissionId);
  if (currentIndex === -1) {
    return orderedIds;
  }
  const targetIndex = currentIndex + offset;
  if (targetIndex < 0 || targetIndex >= next.length) {
    return orderedIds;
  }
  [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
  return next;
};

const AdminSubmissions: React.FC<AdminSubmissionsProps> = ({ user }) => {
  const { t } = useI18n();
  const [folders, setFolders] = useState<SubmissionFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<MoveSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [viewingSubmission, setViewingSubmission] = useState<MoveSubmission | null>(null);
  const [judgingSubmissionId, setJudgingSubmissionId] = useState<string | null>(null);
  const [savingJudgeKey, setSavingJudgeKey] = useState<string | null>(null);
  const [isRefreshingJudgingPanel, setIsRefreshingJudgingPanel] = useState(false);
  const [judgeInitialScoreInput, setJudgeInitialScoreInput] = useState('0');
  const [judgeDraftStatuses, setJudgeDraftStatuses] = useState<SubmissionJudgeStatus[]>([]);
  const [isJudgeDraftDirty, setIsJudgeDraftDirty] = useState(false);
  const [areJudgeDraftsHydrated, setAreJudgeDraftsHydrated] = useState(false);
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<string[]>([]);
  const [submissionRanks, setSubmissionRanks] = useState<Record<string, number>>({});
  const [isBatchExportingA4Pdf, setIsBatchExportingA4Pdf] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [folderPasswordDrafts, setFolderPasswordDrafts] = useState<Record<string, string>>({});
  const [pendingClearFolderPasswordId, setPendingClearFolderPasswordId] = useState<string | null>(null);
  const [submissionSortMode, setSubmissionSortMode] = useState<'manual' | 'latest' | 'initial-asc' | 'initial-desc'>('manual');
  const [manualSubmissionOrder, setManualSubmissionOrder] = useState<string[]>([]);

  const debugRenderCountRef = useRef(0);
  const judgeInitialScoreSyncDebugCountRef = useRef(0);
  const judgeInitialScoreRenderDebugCountRef = useRef(0);
  const latestSubmissionsRequestIdRef = useRef(0);
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const judgeInitialScoreDebugSessionId = 'judge-initial-score';
  const judgingSubmission = useMemo(
    () => (judgingSubmissionId ? submissions.find(submission => submission.id === judgingSubmissionId) || null : null),
    [judgingSubmissionId, submissions]
  );
  // #region debug-point A:report-event
  const reportDebugEvent = (
    hypothesisId: string,
    msg: string,
    data: Record<string, unknown> = {},
    sessionId: string = 'local-folder-create'
  ) => {
    if (!isLocal) return;

    fetch('/api/debug/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        runId: 'pre-fix',
        hypothesisId,
        location: 'components/AdminSubmissions.tsx',
        msg: `[DEBUG] ${msg}`,
        data,
        ts: Date.now()
      })
    }).catch(() => {});
  };
  // #endregion

  const fetchFolders = async () => {
    try {
      setLoading(true);
      // #region debug-point B:fetch-folders-start
      reportDebugEvent('B', 'fetchFolders:start', { folderCountBefore: folders.length });
      // #endregion
      const res = await fetch(isLocal ? '/api/admin/submission-folders' : './api.php?action=admin_get_submission_folders', {
        credentials: 'include'
      });
      const data = await res.json();
      // #region debug-point B:fetch-folders-response
      reportDebugEvent('B', 'fetchFolders:response', {
        ok: res.ok,
        success: data?.success,
        folderCount: Array.isArray(data?.folders) ? data.folders.length : -1,
        error: data?.error || null
      });
      // #endregion
      if (res.ok && data.success) {
        setFolders(data.folders || []);
        setFolderPasswordDrafts(prev => {
          const next = { ...prev };
          (data.folders || []).forEach((folder: SubmissionFolder) => {
            if (typeof next[folder.id] !== 'string') {
              next[folder.id] = '';
            }
          });
          Object.keys(next).forEach((folderId) => {
            if (!(data.folders || []).some((folder: SubmissionFolder) => folder.id === folderId)) {
              delete next[folderId];
            }
          });
          return next;
        });
      }
    } catch (err: any) {
      // #region debug-point B:fetch-folders-error
      reportDebugEvent('B', 'fetchFolders:error', { message: err?.message || 'unknown' });
      // #endregion
      setError('Failed to fetch folders');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (folderId: string) => {
    const requestId = latestSubmissionsRequestIdRef.current + 1;
    latestSubmissionsRequestIdRef.current = requestId;
    try {
      setLoading(true);
      const res = await fetch(isLocal ? `/api/admin/submissions?folderId=${folderId}` : `./api.php?action=admin_get_submissions&folderId=${folderId}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (requestId !== latestSubmissionsRequestIdRef.current) {
        return;
      }
      if (res.ok && data.success) {
        const nextSubmissions = Array.isArray(data.submissions) ? data.submissions : [];
        setSubmissions(prev =>
          nextSubmissions.map((incoming: MoveSubmission) =>
            mergeSubmissionByRevision(prev.find(item => item.id === incoming.id), incoming) || incoming
          )
        );
      }
    } catch (err: any) {
      setError('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    debugRenderCountRef.current += 1;
    if (debugRenderCountRef.current <= 20) {
      // #region debug-point C:state-change
      reportDebugEvent('C', 'state:folders-loading-selected', {
        renderCount: debugRenderCountRef.current,
        folderCount: folders.length,
        loading,
        selectedFolder
      });
      // #endregion
    }
  }, [folders, loading, selectedFolder]);

  useEffect(() => {
    if (selectedFolder) {
      fetchSubmissions(selectedFolder);
    } else {
      setSubmissions([]);
    }
  }, [selectedFolder]);

  useEffect(() => {
    setManualSubmissionOrder([]);
  }, [selectedFolder]);

  useEffect(() => {
    setSelectedSubmissionIds(prev => prev.filter(id => submissions.some(submission => submission.id === id)));
    setSubmissionRanks(prev => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => submissions.some(submission => submission.id === id))
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [submissions]);

  useEffect(() => {
    const latestIds = [...submissions]
      .sort((a, b) => b.createdAt - a.createdAt)
      .map(submission => submission.id);

    setManualSubmissionOrder(prev => {
      const preservedIds = prev.filter(id => latestIds.includes(id));
      const remainingIds = latestIds.filter(id => !preservedIds.includes(id));
      const next = [...preservedIds, ...remainingIds];
      return areStringArraysEqual(prev, next) ? prev : next;
    });
  }, [submissions]);

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    
    try {
      setLoading(true);
      // #region debug-point D:create-folder-start
      reportDebugEvent('D', 'createFolder:start', {
        name: newFolderName,
        folderCountBefore: folders.length
      });
      // #endregion
      const res = await fetch(isLocal ? '/api/admin/submission-folders' : './api.php?action=admin_create_submission_folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newFolderName })
      });
      const data = await res.json();
      // #region debug-point D:create-folder-response
      reportDebugEvent('D', 'createFolder:response', {
        ok: res.ok,
        success: data?.success,
        folderId: data?.folder?.id || null,
        folderName: data?.folder?.name || null,
        error: data?.error || null
      });
      // #endregion
      if (res.ok && data.success) {
        // #region debug-point E:create-folder-set-state
        reportDebugEvent('E', 'createFolder:setFolders', {
          previousFolderCount: folders.length,
          nextFolderCount: [...folders, data.folder].length
        });
        // #endregion
        setFolders([...folders, data.folder]);
        setNewFolderName('');
      } else {
        alert(data.error || 'Failed to create folder');
      }
    } catch (err: any) {
      // #region debug-point D:create-folder-error
      reportDebugEvent('D', 'createFolder:error', { message: err?.message || 'unknown' });
      // #endregion
      alert('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (id: string) => {
    if (!confirm('Are you sure you want to delete this folder and all its submissions?')) return;
    try {
      setLoading(true);
      const res = await fetch(isLocal ? `/api/admin/submission-folders/${id}/delete` : `./api.php?action=admin_delete_submission_folder&id=${id}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setFolders(folders.filter(f => f.id !== id));
        if (selectedFolder === id) setSelectedFolder(null);
      }
    } catch (err: any) {
      alert('Network error');
    } finally {
      setLoading(false);
    }
  };

  const startRenameFolder = (id: string, currentName: string) => {
    setEditingFolderId(id);
    setEditingFolderName(currentName);
  };

  const cancelRenameFolder = () => {
    setEditingFolderId(null);
    setEditingFolderName('');
  };

  const handleRenameFolder = async (id: string, currentName: string) => {
    const nextName = editingFolderName.trim();
    if (!nextName) {
      alert('Folder name is required / 文件夹名称不能为空');
      return;
    }
    if (nextName === currentName) {
      cancelRenameFolder();
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        isLocal
          ? `/api/admin/submission-folders/${id}/update`
          : `./api.php?action=admin_update_submission_folder&id=${id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: nextName })
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to rename folder');
        return;
      }
      setFolders(prev => prev.map(folder => folder.id === id ? data.folder : folder));
      if (pendingClearFolderPasswordId === id) {
        setPendingClearFolderPasswordId(null);
      }
      cancelRenameFolder();
    } catch (err: any) {
      alert(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFolderSubmission = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetch(
        isLocal
          ? `/api/admin/submission-folders/${id}/toggle-submission`
          : `./api.php?action=admin_toggle_submission_folder&id=${id}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to update folder submission status');
        return;
      }
      setFolders(prev => prev.map(folder => folder.id === id ? data.folder : folder));
      if (pendingClearFolderPasswordId === id) {
        setPendingClearFolderPasswordId(null);
      }
    } catch (err: any) {
      alert(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFolderPassword = async (id: string) => {
    const password = (folderPasswordDrafts[id] || '').trim();
    if (!password) {
      alert('Password is required / 密码不能为空');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        isLocal
          ? `/api/admin/submission-folders/${id}/update`
          : `./api.php?action=admin_update_submission_folder&id=${id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ password })
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to update folder password');
        return;
      }
      setFolders(prev => prev.map(folder => folder.id === id ? data.folder : folder));
      setFolderPasswordDrafts(prev => ({ ...prev, [id]: '' }));
      setPendingClearFolderPasswordId(null);
    } catch (err: any) {
      alert(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFolderPassword = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetch(
        isLocal
          ? `/api/admin/submission-folders/${id}/update`
          : `./api.php?action=admin_update_submission_folder&id=${id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ clearPassword: true })
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to clear folder password');
        return;
      }
      setFolders(prev => prev.map(folder => folder.id === id ? data.folder : folder));
      setFolderPasswordDrafts(prev => ({ ...prev, [id]: '' }));
      setPendingClearFolderPasswordId(null);
    } catch (err: any) {
      alert(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubmission = async (id: string) => {
    if (!confirm('Delete this submission?')) return;
    try {
      setLoading(true);
      const res = await fetch(isLocal ? `/api/admin/submissions/${id}/delete` : `./api.php?action=admin_delete_submission&id=${id}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setSubmissions(submissions.filter(s => s.id !== id));
      }
    } catch (err: any) {
      alert('Network error');
    } finally {
      setLoading(false);
    }
  };

  const getJudgeScoreEntry = (submission: MoveSubmission): SubmissionJudgeScore => {
    const existing = submission.judgeScores?.find(score => score.judgeId === user.uid);
    const submissionInitialScore = getSubmissionInitialScore(submission);
    if (existing) {
      const normalizedStatuses = [...existing.trickStatuses];
      while (normalizedStatuses.length < submission.tricks.length) normalizedStatuses.push('pending');
      let normalizedInitialScore = typeof existing.initialScore === 'number' && Number.isFinite(existing.initialScore)
        ? existing.initialScore
        : submissionInitialScore;
      if (shouldUseSubmissionInitialScore(submissionInitialScore, normalizedInitialScore, normalizedStatuses as SubmissionJudgeStatus[], existing.totalScore)) {
        normalizedInitialScore = submissionInitialScore;
      }
      return {
        ...existing,
        judgeName: existing.judgeName || user.displayName || user.username || `Judge ${user.uid}`,
        trickStatuses: normalizedStatuses as SubmissionJudgeStatus[],
        initialScore: normalizedInitialScore,
        totalScore: calculateJudgeScore(submission.tricks, normalizedStatuses as SubmissionJudgeStatus[], normalizedInitialScore)
      };
    }

    const trickStatuses = getDefaultJudgeStatuses(submission.tricks.length);
    return {
      judgeId: user.uid,
      judgeName: user.displayName || user.username || `Judge ${user.uid}`,
      initialScore: submissionInitialScore,
      trickStatuses,
      totalScore: calculateJudgeScore(submission.tricks, trickStatuses, submissionInitialScore),
      updatedAt: 0
    };
  };

  useEffect(() => {
    if (!judgingSubmission) {
      // #region debug-point H1:judge-initial-sync-reset
      reportDebugEvent('H1', 'judgeInitialScore:sync-reset', {
        judgeInitialScoreInputBefore: judgeInitialScoreInput
      }, judgeInitialScoreDebugSessionId);
      // #endregion
      setJudgeInitialScoreInput('0');
      setJudgeDraftStatuses([]);
      setIsJudgeDraftDirty(false);
      setAreJudgeDraftsHydrated(false);
      return;
    }
    const nextJudgeScore = getJudgeScoreEntry(judgingSubmission);
    const storedDrafts = readAdminSubmissionJudgeDrafts(user.uid);
    const storedDraft = storedDrafts[judgingSubmission.id];
    const nextInitialScore = storedDraft?.initialScoreInput ?? String(nextJudgeScore.initialScore ?? 0);
    const nextStatuses = storedDraft?.trickStatuses?.length
      ? [...storedDraft.trickStatuses]
      : [...(nextJudgeScore.trickStatuses || [])];
    while (nextStatuses.length < judgingSubmission.tricks.length) nextStatuses.push('pending');
    nextStatuses.length = judgingSubmission.tricks.length;
    if (judgeInitialScoreSyncDebugCountRef.current < 20) {
      judgeInitialScoreSyncDebugCountRef.current += 1;
      // #region debug-point H2:judge-initial-sync
      reportDebugEvent('H2', 'judgeInitialScore:sync', {
        count: judgeInitialScoreSyncDebugCountRef.current,
        judgingSubmissionId: judgingSubmission.id,
        judgeInitialScoreInputBefore: judgeInitialScoreInput,
        nextInitialScore,
        judgeScoresCount: judgingSubmission.judgeScores?.length || 0,
        submissionInitialScore: judgingSubmission.initialScore ?? null
      }, judgeInitialScoreDebugSessionId);
      // #endregion
    }
    setJudgeInitialScoreInput(nextInitialScore);
    setJudgeDraftStatuses(nextStatuses as SubmissionJudgeStatus[]);
    setIsJudgeDraftDirty(Boolean(storedDraft));
    setAreJudgeDraftsHydrated(true);
  }, [judgingSubmission, submissions, user.uid]);

  useEffect(() => {
    if (!judgingSubmission || !areJudgeDraftsHydrated) return;
    const currentJudgeScore = getJudgeScoreEntry(judgingSubmission);
    const normalizedInput = judgeInitialScoreInput.trim() === '' ? 0 : Number(judgeInitialScoreInput);
    const normalizedStatuses = judgeDraftStatuses.length > 0
      ? [...judgeDraftStatuses]
      : [...currentJudgeScore.trickStatuses];
    while (normalizedStatuses.length < judgingSubmission.tricks.length) normalizedStatuses.push('pending');
    normalizedStatuses.length = judgingSubmission.tricks.length;

    const hasDraftChanges = isJudgeDraftDirty
      || (Number.isFinite(normalizedInput) ? normalizedInput : (currentJudgeScore.initialScore ?? 0)) !== (currentJudgeScore.initialScore ?? 0)
      || !areJudgeStatusesEqual(normalizedStatuses as SubmissionJudgeStatus[], currentJudgeScore.trickStatuses);

    const allDrafts = readAdminSubmissionJudgeDrafts(user.uid);
    if (!hasDraftChanges) {
      if (allDrafts[judgingSubmission.id]) {
        delete allDrafts[judgingSubmission.id];
        writeAdminSubmissionJudgeDrafts(user.uid, allDrafts);
      }
      return;
    }

    allDrafts[judgingSubmission.id] = {
      initialScoreInput: judgeInitialScoreInput,
      trickStatuses: normalizedStatuses as SubmissionJudgeStatus[]
    };
    writeAdminSubmissionJudgeDrafts(user.uid, allDrafts);
  }, [
    areJudgeDraftsHydrated,
    isJudgeDraftDirty,
    judgeDraftStatuses,
    judgeInitialScoreInput,
    judgingSubmission,
    submissions,
    user.uid
  ]);

  const saveJudgeScoreUpdate = async (
    submission: MoveSubmission,
    payload: { trickIndex?: number; status?: SubmissionJudgeStatus; initialScore?: number; trickStatuses?: SubmissionJudgeStatus[] },
    saveKey: string
  ) => {
    try {
      setSavingJudgeKey(saveKey);
      // #region debug-point K:judge-click-start
      reportDebugEvent('H1', 'judgeClick:start', {
        submissionId: submission.id,
        trickIndex: payload.trickIndex ?? null,
        nextStatus: payload.status ?? null,
        initialScore: payload.initialScore ?? null,
        trickStatusesLength: Array.isArray(payload.trickStatuses) ? payload.trickStatuses.length : null,
        currentJudgeScores: submission.judgeScores?.length || 0
      }, 'submission-judge-crash');
      // #endregion
      const res = await fetch(
        isLocal ? `/api/admin/submissions/${submission.id}/judge` : `./api.php?action=admin_judge_submission&id=${submission.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );
      const data = await res.json();
      // #region debug-point K:judge-click-response
      reportDebugEvent('H2', 'judgeClick:response', {
        ok: res.ok,
        success: data?.success,
        hasSubmission: !!data?.submission,
        judgeScoresCount: data?.submission?.judgeScores?.length || 0,
        responseJudgeScoreTotal: data?.judgeScore?.totalScore ?? null,
        error: data?.error || null
      }, 'submission-judge-crash');
      // #endregion
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to save judge status');
        return;
      }

      // #region debug-point L:judge-click-set-state
      reportDebugEvent('H4', 'judgeClick:setState', {
        submissionId: submission.id,
        updatedJudgeStatusesLength: data?.judgeScore?.trickStatuses?.length || 0,
        trickCount: submission.tricks.length
      }, 'submission-judge-crash');
      // #endregion
      setSubmissions(prev =>
        prev.map(item =>
          item.id === submission.id
            ? (mergeSubmissionByRevision(item, data.submission || item) || item)
            : item
        )
      );
      return true;
    } catch (err: any) {
      alert(err?.message || 'Network error');
      return false;
    } finally {
      setSavingJudgeKey(null);
    }
  };

  const handleJudgeStatusUpdate = (_submission: MoveSubmission, trickIndex: number, nextStatus: SubmissionJudgeStatus) => {
    setJudgeDraftStatuses(prev => {
      const next = [...prev];
      while (next.length <= trickIndex) next.push('pending');
      next[trickIndex] = nextStatus;
      return next as SubmissionJudgeStatus[];
    });
    setIsJudgeDraftDirty(true);
  };

  const handleJudgeScoreCardSubmit = async (submission: MoveSubmission) => {
    const normalized = judgeInitialScoreInput.trim() === '' ? 0 : Number(judgeInitialScoreInput);
    // #region debug-point H3:judge-initial-save-start
    reportDebugEvent('H3', 'judgeInitialScore:save-start', {
      submissionId: submission.id,
      judgeInitialScoreInput,
      normalized,
      currentInitialScore: getJudgeScoreEntry(submission).initialScore ?? 0,
      submissionInitialScore: submission.initialScore ?? null
    }, judgeInitialScoreDebugSessionId);
    // #endregion
    if (!Number.isFinite(normalized)) {
      alert('Initial score must be a valid number / 初始分必须是有效数字');
      setJudgeInitialScoreInput(String(getJudgeScoreEntry(submission).initialScore ?? 0));
      return;
    }

    const currentJudgeScore = getJudgeScoreEntry(submission);
    const nextStatuses = judgeDraftStatuses.length > 0
      ? [...judgeDraftStatuses]
      : [...currentJudgeScore.trickStatuses];
    while (nextStatuses.length < submission.tricks.length) nextStatuses.push('pending');

    const hasChanges =
      normalized !== (currentJudgeScore.initialScore ?? 0) ||
      !areJudgeStatusesEqual(nextStatuses as SubmissionJudgeStatus[], currentJudgeScore.trickStatuses);

    if (!hasChanges) {
      setJudgeInitialScoreInput(String(normalized));
      return;
    }

    const saved = await saveJudgeScoreUpdate(
      submission,
      {
        initialScore: normalized,
        trickStatuses: nextStatuses as SubmissionJudgeStatus[]
      },
      `${submission.id}:submit-card`
    );

    // #region debug-point H4:judge-initial-save-finish
    reportDebugEvent('H4', 'judgeInitialScore:save-finish', {
      submissionId: submission.id,
      saved,
      judgeInitialScoreInputAfterSave: judgeInitialScoreInput,
      normalized
    }, judgeInitialScoreDebugSessionId);
    // #endregion

    if (saved) {
      clearAdminSubmissionJudgeDraft(user.uid, submission.id);
      setJudgeInitialScoreInput(String(normalized));
      setJudgeDraftStatuses(nextStatuses as SubmissionJudgeStatus[]);
      setIsJudgeDraftDirty(false);
    }
  };

  const handleJudgeScoreReorder = async (submission: MoveSubmission, judgeIds: string[]) => {
    if (judgeIds.length <= 1) return;

    try {
      setSavingJudgeKey(`${submission.id}:judge-order`);
      const res = await fetch(
        isLocal ? `/api/admin/submissions/${submission.id}/judge-order` : `./api.php?action=admin_reorder_submission_judges&id=${submission.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ judgeIds })
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to reorder judge cards');
        return;
      }

      setSubmissions(prev =>
        prev.map(item =>
          item.id === submission.id
            ? (mergeSubmissionByRevision(item, data.submission || item) || item)
            : item
        )
      );
    } catch (err: any) {
      alert(err?.message || 'Network error');
    } finally {
      setSavingJudgeKey(null);
    }
  };

  const handleRefreshJudgingPanel = async () => {
    if (!selectedFolder) return;

    try {
      setIsRefreshingJudgingPanel(true);
      await fetchSubmissions(selectedFolder);
    } finally {
      setIsRefreshingJudgingPanel(false);
    }
  };

  const handleSelectFinalJudgeScore = (submission: MoveSubmission, judgeId: string) => {
    const judgeScores = submission.judgeScores || [];
    if (judgeScores.length <= 1 || judgeScores[0]?.judgeId === judgeId) {
      return;
    }

    const nextJudgeIds = [
      judgeId,
      ...judgeScores
        .map(score => score.judgeId)
        .filter(existingJudgeId => existingJudgeId !== judgeId)
    ];

    handleJudgeScoreReorder(submission, nextJudgeIds);
  };

  const toggleSubmissionSelection = (submissionId: string) => {
    setSelectedSubmissionIds(prev =>
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const handleSelectAllSubmissions = () => {
    if (selectedSubmissionIds.length === submissions.length) {
      setSelectedSubmissionIds([]);
      setSubmissionRanks({});
      return;
    }
    setSelectedSubmissionIds(submissions.map(submission => submission.id));
  };

  const getRankedSelectedSubmissions = () => {
    const rankedItems = submissions
      .filter(submission => selectedSubmissionIds.includes(submission.id))
      .map(submission => ({
        submission,
        totalScore: getFinalSubmissionScore(submission)
      }))
      .filter(item => item.totalScore !== null) as Array<{ submission: MoveSubmission; totalScore: number }>;

    if (rankedItems.length === 0) {
      return [];
    }

    const sorted = [...rankedItems].sort((a, b) => {
      if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
      return a.submission.createdAt - b.submission.createdAt;
    });

    let previousScore: number | null = null;
    let currentRank = 0;

    return sorted.map((item, index) => {
      if (previousScore === null || item.totalScore !== previousScore) {
        currentRank = index + 1;
        previousScore = item.totalScore;
      }

      return {
        ...item,
        rank: currentRank
      };
    });
  };

  const handleRankSelectedSubmissions = () => {
    const rankedItems = getRankedSelectedSubmissions();

    if (rankedItems.length === 0) {
      alert('Please score the selected submissions first / 请先为已选决赛表打分');
      return;
    }

    const nextRanks: Record<string, number> = {};
    rankedItems.forEach(item => {
      nextRanks[item.submission.id] = item.rank;
    });

    setSubmissionRanks(nextRanks);
  };

  const handleExportRankedExcel = () => {
    const rankedItems = getRankedSelectedSubmissions();

    if (rankedItems.length === 0) {
      alert('Please score the selected submissions first / 请先为已选决赛表打分');
      return;
    }

    const rows = rankedItems.map(item => {
      const breakdown = getFinalSubmissionScoreBreakdown(item.submission);
      return {
        'Rank / 名次': item.rank,
        'Total Score / 总分': item.totalScore,
        'Initial Score / 初始分': getDisplayedSubmissionInitialScore(item.submission, user.uid),
        'Square Score / 乘算分': breakdown?.baseSum ?? '',
        'All Success Bonus / 全部成功加算分数': breakdown?.allSuccessBonus ?? '',
        'Name / 名字': item.submission.userDisplayName,
        'Competition No. / 比赛编号': getSubmissionCompetitionNumber(item.submission)
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 24 },
      { wch: 24 },
      { wch: 20 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rankings');

    const folderName = folders.find(folder => folder.id === selectedFolder)?.name || 'submissions';
    const safeFolderName = folderName.replace(/[\\/:*?"<>|]/g, '_');
    XLSX.writeFile(workbook, `${safeFolderName}_rankings.xlsx`);
  };

  const orderedSubmissions = useMemo(() => {
    if (submissionSortMode === 'manual') {
      const submissionMap = new Map(submissions.map(submission => [submission.id, submission]));
      const manualIds = manualSubmissionOrder.filter(id => submissionMap.has(id));
      const fallbackIds = [...submissions]
        .sort((a, b) => b.createdAt - a.createdAt)
        .map(submission => submission.id)
        .filter(id => !manualIds.includes(id));

      return [...manualIds, ...fallbackIds]
        .map(id => submissionMap.get(id))
        .filter((submission): submission is MoveSubmission => Boolean(submission));
    }

    return [...submissions].sort((a, b) => {
      if (submissionSortMode === 'initial-asc' || submissionSortMode === 'initial-desc') {
        const initialA = getFinalSubmissionInitialScore(a);
        const initialB = getFinalSubmissionInitialScore(b);
        const direction = submissionSortMode === 'initial-asc' ? 1 : -1;

        if (initialA === null && initialB === null) return b.createdAt - a.createdAt;
        if (initialA === null) return 1;
        if (initialB === null) return -1;
        if (initialA !== initialB) return (initialA - initialB) * direction;
        return b.createdAt - a.createdAt;
      }

      const rankA = submissionRanks[a.id];
      const rankB = submissionRanks[b.id];
      if (typeof rankA === 'number' && typeof rankB === 'number' && rankA !== rankB) return rankA - rankB;
      if (typeof rankA === 'number' && typeof rankB !== 'number') return -1;
      if (typeof rankA !== 'number' && typeof rankB === 'number') return 1;
      return b.createdAt - a.createdAt;
    });
  }, [manualSubmissionOrder, submissionRanks, submissionSortMode, submissions]);

  const handleMoveSubmission = (submissionId: string, offset: -1 | 1) => {
    const currentDisplayOrder = orderedSubmissions.map(submission => submission.id);
    const nextOrder = moveSubmissionIdByOffset(currentDisplayOrder, submissionId, offset);
    if (areStringArraysEqual(currentDisplayOrder, nextOrder)) {
      return;
    }
    setManualSubmissionOrder(nextOrder);
    setSubmissionSortMode('manual');
  };

  const A4_EXPORT_WIDTH = 794;
  const A4_EXPORT_HEIGHT = 1123;
  const A4_TABLE_HEIGHT_BUDGET = 900;
  const A4_TABLE_BOTTOM_SAFETY = 42;
  const A4_ROW_MIN_HEIGHT = 54;
  const A4_TRICK_FONT_MAX = 22;
  const A4_TRICK_FONT_MIN = 14;
  const A4_HEADER_FONT_MAX = 84;
  const A4_HEADER_FONT_MIN = 24;

  type PrintableA4Row = {
    trickName: string;
    trickNumber?: number;
    rowHeight: number;
    estimatedLines: number;
    fontSize: number;
    lineHeight: number;
    isBlank?: boolean;
  };

  const getSubmissionPrintableTricks = (submission: MoveSubmission) => (
    submission.tricks
      .map(trick => String(trick || '').trim())
      .filter(Boolean)
  );

  const getSubmissionA4Pages = (submission: MoveSubmission) => {
    const tricks = getSubmissionPrintableTricks(submission);
    const pages: PrintableA4Row[][] = [];
    let currentPage: PrintableA4Row[] = [];
    let usedHeight = 0;

    tricks.forEach((trickName, index) => {
      const layout = getAdaptiveA4TrickTextStyle(trickName);
      const projectedHeight = usedHeight + layout.rowHeight;
      const rowSafety = A4_TABLE_BOTTOM_SAFETY + Math.max(0, layout.estimatedLines - 1) * 16;
      if (
        currentPage.length > 0 &&
        projectedHeight > (A4_TABLE_HEIGHT_BUDGET - rowSafety)
      ) {
        pages.push(currentPage);
        currentPage = [];
        usedHeight = 0;
      }

      currentPage.push({
        trickName,
        trickNumber: index + 1,
        rowHeight: layout.rowHeight,
        estimatedLines: layout.estimatedLines,
        fontSize: layout.fontSize,
        lineHeight: layout.lineHeight
      });
      usedHeight += layout.rowHeight;
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    if (pages.length === 0) {
      pages.push([]);
    }

    return pages;
  };

  const getPrintableRowsForA4Page = (pageRows: PrintableA4Row[]) => {
    if (pageRows.length > 0) {
      return pageRows;
    }

    return [{
      trickName: '',
      rowHeight: A4_ROW_MIN_HEIGHT,
      estimatedLines: 1,
      fontSize: A4_TRICK_FONT_MAX,
      lineHeight: 1.24,
      isBlank: true
    }];
  };

  const getSafeSubmissionExportBaseName = (submission: MoveSubmission) => {
    const safeCompetitionNumber = getSubmissionCompetitionNumber(submission).replace(/[\\/:*?"<>|]/g, '_');
    const safeDisplayName = String(submission.userDisplayName || 'player').replace(/[\\/:*?"<>|]/g, '_');
    return `A4_${safeCompetitionNumber}_${safeDisplayName}`;
  };

  const getSafeSubmissionPdfFileName = (submission: MoveSubmission) => {
    const safeDisplayName = String(submission.userDisplayName || 'player').replace(/[\\/:*?"<>|]/g, '_');
    const safeCompetitionNumber = getSubmissionCompetitionNumber(submission).replace(/[\\/:*?"<>|]/g, '_');
    return `${safeDisplayName}_${safeCompetitionNumber}.pdf`;
  };

  const getSafeBatchExportFolderName = () => {
    const selectedFolderName = folders.find(folder => folder.id === selectedFolder)?.name || 'submissions';
    return `A4_PDF_${String(selectedFolderName).replace(/[\\/:*?"<>|]/g, '_')}`;
  };

  const waitForDomPaint = () => new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

  const getAdaptiveA4HeaderFontSize = (text: string) => {
    const length = String(text || '').trim().length;
    if (length > 28) return 34;
    if (length > 22) return 42;
    if (length > 16) return 54;
    if (length > 10) return 66;
    return A4_HEADER_FONT_MAX;
  };

  const getAdaptiveA4TrickTextStyle = (text: string) => {
    const normalized = String(text || '');
    const lines = normalized.split('\n');
    const totalLength = normalized.replace(/\s+/g, '').length;
    const visualUnits = lines.reduce((sum, line) => {
      const trimmed = line.trim();
      if (!trimmed) return sum;
      const lineUnits = Array.from(trimmed).reduce((lineSum, char) => {
        if (/\s/.test(char)) return lineSum + 0.18;
        if (/[A-Za-z0-9]/.test(char)) return lineSum + 0.52;
        if (/[,./\\\-()]/.test(char)) return lineSum + 0.28;
        return lineSum + 1;
      }, 0);
      return sum + lineUnits;
    }, 0);
    const estimatedLines = Math.max(1, Math.ceil(visualUnits / 31));

    let fontSize = A4_TRICK_FONT_MAX;
    let lineHeight = 1.24;

    if (estimatedLines >= 5 || totalLength > 180) {
      fontSize = 16;
      lineHeight = 1.18;
    } else if (estimatedLines >= 4 || totalLength > 140) {
      fontSize = 17;
      lineHeight = 1.18;
    } else if (estimatedLines >= 3 || totalLength > 104) {
      fontSize = 18;
      lineHeight = 1.18;
    } else if (estimatedLines >= 2 || totalLength > 62) {
      fontSize = 19;
      lineHeight = 1.18;
    } else if (totalLength > 38) {
      fontSize = 21;
      lineHeight = 1.2;
    }

    const rowHeight = Math.max(
      A4_ROW_MIN_HEIGHT,
      Math.ceil((fontSize * lineHeight * estimatedLines) + 30)
    );

    return { estimatedLines, rowHeight, fontSize, lineHeight };
  };

  const fitTextToBox = (
    element: HTMLElement,
    options: {
      maxFontSize: number;
      minFontSize: number;
      lineHeight: number;
      minLineHeight?: number;
      heightSafetyPx?: number;
      widthSafetyPx?: number;
      checkWidth?: boolean;
      startFontSize?: number;
      startLineHeight?: number;
    }
  ) => {
    let fontSize = options.startFontSize ?? options.maxFontSize;
    let lineHeight = options.startLineHeight ?? options.lineHeight;
    const minLineHeight = options.minLineHeight ?? options.lineHeight;
    const heightSafetyPx = options.heightSafetyPx ?? 0;
    const widthSafetyPx = options.widthSafetyPx ?? 0;
    const checkWidth = options.checkWidth ?? false;
    element.style.fontSize = `${fontSize}px`;
    element.style.lineHeight = String(lineHeight);
    element.style.overflow = 'hidden';

    while (
      element.scrollHeight > Math.max(0, element.clientHeight - heightSafetyPx) ||
      (checkWidth && element.scrollWidth > Math.max(0, element.clientWidth - widthSafetyPx))
    ) {
      if (fontSize > options.minFontSize) {
        fontSize -= 1;
      } else if (lineHeight > minLineHeight) {
        lineHeight = Math.max(minLineHeight, Number((lineHeight - 0.02).toFixed(2)));
      } else {
        break;
      }
      element.style.fontSize = `${fontSize}px`;
      element.style.lineHeight = String(lineHeight);
    }
  };

  const fitPrintableA4PageElement = (pageElement: HTMLElement) => {
    pageElement.querySelectorAll<HTMLElement>('[data-a4-fit="header"]').forEach((element) => {
      const startFontSize = Number(element.dataset.a4FontSize || A4_HEADER_FONT_MAX);
      const startLineHeight = Number(element.dataset.a4LineHeight || 1.2);
      fitTextToBox(element, {
        maxFontSize: A4_HEADER_FONT_MAX,
        minFontSize: A4_HEADER_FONT_MIN,
        lineHeight: startLineHeight,
        minLineHeight: 1.1,
        heightSafetyPx: 10,
        startFontSize,
        startLineHeight
      });
    });

    pageElement.querySelectorAll<HTMLElement>('[data-a4-fit="trick"]').forEach((element) => {
      const startFontSize = Number(element.dataset.a4FontSize || A4_TRICK_FONT_MAX);
      const startLineHeight = Number(element.dataset.a4LineHeight || 1.24);
      fitTextToBox(element, {
        maxFontSize: A4_TRICK_FONT_MAX,
        minFontSize: A4_TRICK_FONT_MIN,
        lineHeight: startLineHeight,
        minLineHeight: 1.16,
        heightSafetyPx: 4,
        startFontSize,
        startLineHeight
      });
    });
  };

  const createPrintableA4PageElement = (
    submission: MoveSubmission,
    pageRows: PrintableA4Row[]
  ) => {
    const page = document.createElement('div');
    page.style.cssText = [
      `width:${A4_EXPORT_WIDTH}px`,
      `height:${A4_EXPORT_HEIGHT}px`,
      'background:#ffffff',
      'color:#000000',
      'box-sizing:border-box',
      'font-family:"Microsoft YaHei","PingFang SC",sans-serif'
    ].join(';');

    const frame = document.createElement('div');
    frame.style.cssText = [
      'display:flex',
      'height:100%',
      'flex-direction:column',
      'border:3px solid #000000',
      'padding:40px',
      'box-sizing:border-box'
    ].join(';');
    page.appendChild(frame);

    const header = document.createElement('div');
    header.style.cssText = [
      'display:grid',
      'grid-template-columns:180px 1fr',
      'column-gap:20px',
      'row-gap:16px',
      'padding-bottom:24px',
      'border-bottom:3px solid #000000'
    ].join(';');
    frame.appendChild(header);

    const createHeaderCard = (label: string, value: string) => {
      const headerFontSize = getAdaptiveA4HeaderFontSize(value);
      const card = document.createElement('div');
      card.style.cssText = 'border:2px solid #000000;padding:12px 16px;box-sizing:border-box;height:96px;';

      const labelEl = document.createElement('div');
      labelEl.textContent = label;
      labelEl.style.cssText = 'font-size:12px;font-weight:700;letter-spacing:0.2em;';
      card.appendChild(labelEl);

      const valueEl = document.createElement('div');
      valueEl.textContent = value;
      valueEl.setAttribute('data-a4-fit', 'header');
      valueEl.dataset.a4FontSize = String(headerFontSize);
      valueEl.dataset.a4LineHeight = '1.12';
      valueEl.style.cssText = `margin-top:10px;height:52px;font-size:${headerFontSize}px;font-weight:900;line-height:1.05;word-break:break-word;overflow-wrap:anywhere;white-space:pre-wrap;overflow:hidden;padding:0;`;
      card.appendChild(valueEl);

      return card;
    };

    header.appendChild(createHeaderCard('比赛编号', getSubmissionCompetitionNumber(submission)));
    header.appendChild(createHeaderCard('昵称', submission.userDisplayName || '--'));

    const table = document.createElement('div');
    table.style.cssText = 'margin-top:24px;overflow:hidden;border:2px solid #000000;display:flex;flex-direction:column;';
    frame.appendChild(table);

    const printableRows = getPrintableRowsForA4Page(pageRows);
    printableRows.forEach((rowData, index) => {
      const row = document.createElement('div');
      row.style.cssText = [
        'display:grid',
        'grid-template-columns:72px 1fr 88px',
        `min-height:${rowData.rowHeight}px`,
        'border-bottom:1px solid #000000',
        'box-sizing:border-box'
      ].join(';');
      if (index === printableRows.length - 1) {
        row.style.borderBottom = '0';
      }

      const indexCell = document.createElement('div');
      indexCell.textContent = rowData.trickNumber ? String(rowData.trickNumber) : '';
      indexCell.style.cssText = 'display:flex;align-items:center;justify-content:center;border-right:1px solid #000000;font-size:30px;font-weight:900;';
      row.appendChild(indexCell);

      const trickCell = document.createElement('div');
      trickCell.setAttribute('data-a4-fit', 'trick');
      trickCell.dataset.a4FontSize = String(rowData.fontSize);
      trickCell.dataset.a4LineHeight = String(rowData.lineHeight);
      trickCell.textContent = rowData.trickName || '';
      trickCell.style.cssText = `border-right:1px solid #000000;padding:10px 12px 14px;font-size:${rowData.fontSize}px;font-weight:700;line-height:${rowData.lineHeight};word-break:break-word;overflow-wrap:anywhere;white-space:pre-wrap;overflow:hidden;box-sizing:border-box;height:100%;`;
      row.appendChild(trickCell);

      const markCell = document.createElement('div');
      markCell.style.cssText = 'display:flex;align-items:center;justify-content:center;padding:12px;';
      if (rowData.trickName) {
        const markBox = document.createElement('div');
        markBox.style.cssText = 'width:36px;height:36px;border:2px solid #000000;box-sizing:border-box;';
        markCell.appendChild(markBox);
      }
      row.appendChild(markCell);

      table.appendChild(row);
    });

    return page;
  };

  const captureSubmissionA4Canvases = async (submission: MoveSubmission) => {
    const pages = getSubmissionA4Pages(submission);
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-10000px;top:0;opacity:0;pointer-events:none;z-index:-1;';
    document.body.appendChild(host);

    try {
      if (document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }

      const canvases: HTMLCanvasElement[] = [];
      for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
        const pageElement = createPrintableA4PageElement(submission, pages[pageIndex]);
        host.appendChild(pageElement);
        await waitForDomPaint();
        fitPrintableA4PageElement(pageElement);
        await waitForDomPaint();
        const canvas = await html2canvas(pageElement, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        });
        canvases.push(canvas);
        host.removeChild(pageElement);
      }
      return canvases;
    } finally {
      document.body.removeChild(host);
    }
  };

  const renderPrintableA4Page = (
    submission: MoveSubmission,
    pageRows: PrintableA4Row[],
    pageIndex: number
  ) => {
    const printableRows = getPrintableRowsForA4Page(pageRows);
    const competitionNumberFontSize = getAdaptiveA4HeaderFontSize(getSubmissionCompetitionNumber(submission));
    const nicknameFontSize = getAdaptiveA4HeaderFontSize(submission.userDisplayName || '--');

    return (
      <div
        className="bg-white text-black shadow-[0_18px_60px_rgba(0,0,0,0.28)]"
        style={{
          width: A4_EXPORT_WIDTH,
          height: A4_EXPORT_HEIGHT,
          fontFamily: '"Microsoft YaHei", "PingFang SC", sans-serif'
        }}
      >
        <div className="flex h-full flex-col border-[3px] border-black px-10 py-10">
          <div className="grid grid-cols-[180px_1fr] gap-x-5 gap-y-4 border-b-[3px] border-black pb-6">
            <div className="border-2 border-black px-4 py-3 h-24">
              <div className="text-xs font-bold tracking-[0.2em]">比赛编号</div>
              <div
                className="mt-3 break-all font-black overflow-hidden"
                style={{ fontSize: `${competitionNumberFontSize}px`, lineHeight: 1.05, height: '52px', padding: 0, marginTop: '10px' }}
              >
                {getSubmissionCompetitionNumber(submission)}
              </div>
            </div>
            <div className="border-2 border-black px-4 py-3 h-24">
              <div className="text-xs font-bold tracking-[0.2em]">昵称</div>
              <div
                className="mt-3 break-all font-black leading-tight overflow-hidden"
                style={{ fontSize: `${nicknameFontSize}px`, lineHeight: 1.05, height: '52px', padding: 0, marginTop: '10px' }}
              >
                {submission.userDisplayName || '--'}
              </div>
            </div>
          </div>

          <div className="mt-6 overflow-hidden border-2 border-black">
            {printableRows.map((rowData, index) => (
              (() => {
                return (
                  <div
                    key={`${pageIndex + 1}-${index + 1}-${rowData.trickNumber ?? 'blank'}`}
                    className="grid border-b border-black last:border-b-0"
                    style={{
                      gridTemplateColumns: '72px 1fr 88px',
                      minHeight: `${rowData.rowHeight}px`
                    }}
                  >
                    <div className="flex items-center justify-center border-r border-black text-[30px] font-black">
                      {rowData.trickNumber ?? ''}
                    </div>
                    <div
                      className="border-r border-black px-3 pt-2.5 pb-3.5 font-bold break-words whitespace-pre-wrap overflow-hidden box-border h-full"
                      style={{ fontSize: `${rowData.fontSize}px`, lineHeight: rowData.lineHeight }}
                    >
                      {rowData.trickName || '\u00A0'}
                    </div>
                    <div className="flex items-center justify-center p-3">
                      {rowData.trickName ? <div className="h-9 w-9 border-2 border-black" /> : null}
                    </div>
                  </div>
                );
              })()
            ))}
          </div>
        </div>
      </div>
    );
  };

  const openPrintableA4Preview = (submission: MoveSubmission) => {
    setViewingSubmission(submission);
  };

  const handleExportA4JPG = async (submission: MoveSubmission) => {
    try {
      const canvases = await captureSubmissionA4Canvases(submission);
      const baseName = getSafeSubmissionExportBaseName(submission);

      canvases.forEach((canvas, index) => {
        const link = document.createElement('a');
        const suffix = canvases.length > 1 ? `_p${index + 1}` : '';
        link.download = `${baseName}${suffix}.jpg`;
        link.href = canvas.toDataURL('image/jpeg', 0.96);
        link.click();
      });
    } catch (err) {
      console.error('A4 export failed', err);
      alert('Failed to generate A4 JPG');
    }
  };

  const handleBatchExportA4Pdf = async () => {
    const selectedSubmissions = orderedSubmissions.filter(submission => selectedSubmissionIds.includes(submission.id));
    if (selectedSubmissions.length === 0) {
      alert('Please select at least one submission / 请至少选择一个投稿');
      return;
    }

    setIsBatchExportingA4Pdf(true);
    try {
      const zip = new JSZip();
      const exportFolder = zip.folder(getSafeBatchExportFolderName());
      if (!exportFolder) {
        throw new Error('Failed to create export folder');
      }

      for (const submission of selectedSubmissions) {
        const canvases = await captureSubmissionA4Canvases(submission);
        if (canvases.length === 0) continue;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'pt',
          format: 'a4',
          compress: true,
        });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        canvases.forEach((canvas, index) => {
          if (index > 0) {
            pdf.addPage('a4', 'portrait');
          }
          pdf.addImage(canvas.toDataURL('image/jpeg', 0.96), 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
        });
        exportFolder.file(getSafeSubmissionPdfFileName(submission), pdf.output('arraybuffer'));
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = URL.createObjectURL(zipBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = zipUrl;
      downloadLink.download = `${getSafeBatchExportFolderName()}.zip`;
      downloadLink.click();
      await new Promise(resolve => setTimeout(resolve, 240));
      URL.revokeObjectURL(zipUrl);
    } catch (err) {
      console.error('Batch A4 PDF export failed', err);
      alert('Failed to batch export A4 PDFs / 批量导出 A4 PDF 失败');
    } finally {
      setIsBatchExportingA4Pdf(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:h-full gap-4 relative">
      {/* Left Sidebar: Folders */}
      <div className="w-full lg:w-[320px] lg:max-w-[320px] bg-black/40 border border-blue-500/20 rounded-xl p-4 flex flex-col gap-4 lg:overflow-y-auto custom-scrollbar shrink-0">
        <h2 className="text-blue-400 font-orbitron font-bold uppercase tracking-widest text-sm flex justify-between items-center">
          Events
          <button onClick={fetchFolders} className="text-zinc-500 hover:text-white"><RefreshCw size={14} /></button>
        </h2>
        
        <form onSubmit={handleCreateFolder} className="flex gap-2">
          <input
            type="text"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            placeholder="New folder..."
            className="flex-1 bg-black/60 border border-zinc-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
          />
          <button type="submit" disabled={!newFolderName} className="bg-blue-600/30 text-blue-400 border border-blue-500/50 p-1 rounded hover:bg-blue-600/50 disabled:opacity-50">
            <Plus size={16} />
          </button>
        </form>

        <div className="flex flex-col gap-2 mt-2">
          {folders.map(f => {
            const isSubmissionOpen = f.isSubmissionOpen !== false;
            const isEditing = editingFolderId === f.id;
            const hasPassword = !!f.hasPassword;
            const isPendingClearPassword = pendingClearFolderPasswordId === f.id;
            return (
            <div 
              key={f.id}
              onClick={() => { if (!isEditing) setSelectedFolder(f.id); }}
              className={`group flex flex-col gap-3 p-3 rounded cursor-pointer border transition-all ${selectedFolder === f.id ? 'bg-blue-900/30 border-blue-500/50' : 'bg-black/20 border-transparent hover:bg-zinc-800'}`}
            >
              <div className="flex items-start gap-2 min-w-0 w-full">
                <Folder size={16} className={selectedFolder === f.id ? 'text-blue-400 shrink-0' : 'text-zinc-500 shrink-0'} />
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <input
                      value={editingFolderName}
                      onChange={(e) => setEditingFolderName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleRenameFolder(f.id, f.name);
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelRenameFolder();
                        }
                      }}
                      className="w-full bg-black/60 border border-blue-500/40 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-400"
                      autoFocus
                    />
                  ) : (
                    <div className={`text-sm font-rajdhani leading-snug break-all ${selectedFolder === f.id ? 'text-white' : 'text-zinc-400'}`}>{f.name}</div>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-orbitron uppercase tracking-wider ${
                    isSubmissionOpen
                      ? 'bg-green-500/15 border border-green-500/35 text-green-300'
                      : 'bg-red-500/15 border border-red-500/35 text-red-300'
                  } inline-flex mt-1`}>
                    {isSubmissionOpen ? 'Open / 开启' : 'Closed / 关闭'}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-orbitron uppercase tracking-wider ${
                    hasPassword
                      ? 'bg-cyan-500/15 border border-cyan-500/35 text-cyan-200'
                      : 'bg-amber-500/15 border border-amber-500/35 text-amber-200'
                  } inline-flex mt-1 ml-2`}>
                    {hasPassword ? 'Password Set / 已设密码' : 'No Password / 未设密码'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 w-full">
                <input
                  type="password"
                  value={folderPasswordDrafts[f.id] || ''}
                  onChange={(e) => setFolderPasswordDrafts(prev => ({ ...prev, [f.id]: e.target.value }))}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={hasPassword ? 'Set new password / 修改密码' : 'Set folder password / 设置赛事密码'}
                  className="w-full bg-black/60 border border-zinc-700 rounded px-2 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSaveFolderPassword(f.id); }}
                    disabled={isEditing || !(folderPasswordDrafts[f.id] || '').trim()}
                    className={`min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors text-center ${
                      isEditing || !(folderPasswordDrafts[f.id] || '').trim()
                        ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                        : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20'
                    }`}
                  >
                    Save Password / 保存密码
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPendingClearPassword) {
                        handleClearFolderPassword(f.id);
                        return;
                      }
                      setPendingClearFolderPasswordId(f.id);
                    }}
                    disabled={isEditing || !hasPassword}
                    className={`min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors text-center ${
                      isEditing || !hasPassword
                        ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                        : isPendingClearPassword
                          ? 'border-red-500/50 bg-red-500/15 text-red-200 hover:bg-red-500/25'
                          : 'border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
                    }`}
                  >
                    {isPendingClearPassword ? 'Confirm Clear / 确认清空' : 'Clear Password / 清空密码'}
                  </button>
                </div>
                {isPendingClearPassword && hasPassword && !isEditing && (
                  <div className="flex items-center justify-between gap-2 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-[10px] text-red-200 font-orbitron uppercase tracking-wider">
                    <span>Click again to clear this folder password / 再点一次确认清空赛事密码</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingClearFolderPasswordId(null);
                      }}
                      className="shrink-0 rounded border border-zinc-500/40 bg-zinc-900/50 px-2 py-1 text-zinc-300 hover:bg-zinc-800/60 transition-colors"
                    >
                      Cancel / 取消
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 w-full">
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleFolderSubmission(f.id); }}
                  disabled={isEditing}
                  className={`min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors text-center ${
                    isSubmissionOpen
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20'
                      : 'border-green-500/40 bg-green-500/10 text-green-300 hover:bg-green-500/20'
                  } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSubmissionOpen ? 'Close / 关闭' : 'Open / 开启'}
                </button>
                {isEditing ? (
                  <div className="contents">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRenameFolder(f.id, f.name); }}
                      className="min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center justify-center gap-1 text-center"
                    >
                      <Check size={12} /> Save / 保存
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); cancelRenameFolder(); }}
                      className="col-span-2 min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors border-zinc-500/40 bg-zinc-500/10 text-zinc-300 hover:bg-zinc-500/20 flex items-center justify-center gap-1 text-center"
                    >
                      <X size={12} /> Cancel / 取消
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); startRenameFolder(f.id, f.name); }}
                    className="min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 flex items-center justify-center gap-1 text-center"
                  >
                    <Pencil size={12} /> Rename / 重命名
                  </button>
                )}
                <button 
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f.id); }}
                  disabled={isEditing}
                  className="col-span-2 min-w-0 px-2 py-1.5 rounded border text-[10px] font-orbitron uppercase tracking-wider transition-colors border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 flex items-center justify-center gap-1 text-center"
                >
                  <Trash2 size={12} /> Delete / 删除
                </button>
              </div>
            </div>
          )})}
          {folders.length === 0 && !loading && (
            <div className="text-zinc-600 text-sm text-center py-4 font-rajdhani">No folders found.</div>
          )}
        </div>
      </div>

      {/* Right Area: Submissions */}
      <div className="flex-1 min-h-[52vh] md:min-h-[58vh] lg:min-h-0 bg-black/40 border border-blue-500/20 rounded-xl p-4 flex flex-col gap-4 lg:overflow-y-auto custom-scrollbar">
        {selectedFolder ? (
          <>
            <div className="flex flex-col gap-3">
              <h2 className="text-blue-400 font-orbitron font-bold uppercase tracking-widest text-sm">
                Submissions ({submissions.length})
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleSelectAllSubmissions}
                  className="px-3 py-1.5 rounded-lg border border-cyan-400/30 bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 transition-colors text-xs font-orbitron uppercase tracking-wider"
                >
                  {selectedSubmissionIds.length === submissions.length && submissions.length > 0 ? 'Clear All / 取消全选' : 'Select All / 全选'}
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setSubmissionSortMode('manual')}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors ${
                      submissionSortMode === 'manual'
                        ? 'border-cyan-300/50 bg-cyan-300/15 text-cyan-100'
                        : 'border-white/10 bg-white/[0.03] text-zinc-400 hover:border-cyan-400/20 hover:text-cyan-200'
                    }`}
                  >
                    Manual / 手动
                  </button>
                  <button
                    onClick={() => setSubmissionSortMode('latest')}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors ${
                      submissionSortMode === 'latest'
                        ? 'border-cyan-400/40 bg-cyan-400/20 text-cyan-200'
                        : 'border-white/10 bg-white/[0.03] text-zinc-400 hover:border-cyan-400/20 hover:text-cyan-200'
                    }`}
                  >
                    Latest / 最新
                  </button>
                  <button
                    onClick={() => setSubmissionSortMode('initial-asc')}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors ${
                      submissionSortMode === 'initial-asc'
                        ? 'border-fuchsia-400/40 bg-fuchsia-400/20 text-fuchsia-200'
                        : 'border-white/10 bg-white/[0.03] text-zinc-400 hover:border-fuchsia-400/20 hover:text-fuchsia-200'
                    }`}
                  >
                    Initial ↑ / 初始分升序
                  </button>
                  <button
                    onClick={() => setSubmissionSortMode('initial-desc')}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors ${
                      submissionSortMode === 'initial-desc'
                        ? 'border-fuchsia-400/40 bg-fuchsia-400/20 text-fuchsia-200'
                        : 'border-white/10 bg-white/[0.03] text-zinc-400 hover:border-fuchsia-400/20 hover:text-fuchsia-200'
                    }`}
                  >
                    Initial ↓ / 初始分降序
                  </button>
                </div>
                <button
                  onClick={handleRankSelectedSubmissions}
                  disabled={selectedSubmissionIds.length === 0}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors ${
                    selectedSubmissionIds.length === 0
                      ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                      : 'border-yellow-400/30 bg-yellow-400/15 text-yellow-300 hover:bg-yellow-400/25'
                  }`}
                >
                  Rank Selected / 选中排名
                </button>
                <button
                  onClick={handleExportRankedExcel}
                  disabled={selectedSubmissionIds.length === 0}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors flex items-center gap-1 ${
                    selectedSubmissionIds.length === 0
                      ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                      : 'border-emerald-400/30 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25'
                  }`}
                >
                  <Download size={14} /> Export Excel / 导出Excel
                </button>
                <button
                  onClick={handleBatchExportA4Pdf}
                  disabled={selectedSubmissionIds.length === 0 || isBatchExportingA4Pdf}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-orbitron uppercase tracking-wider transition-colors flex items-center gap-1 ${
                    selectedSubmissionIds.length === 0 || isBatchExportingA4Pdf
                      ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                      : 'border-blue-400/30 bg-blue-400/15 text-blue-200 hover:bg-blue-400/25'
                  }`}
                >
                  <Download size={14} /> {isBatchExportingA4Pdf ? '导出中...' : '批量导出A4 PDF'}
                </button>
                <div className="text-[11px] text-zinc-500 font-orbitron uppercase tracking-[0.2em]">
                  Selected {selectedSubmissionIds.length} / {submissions.length}
                </div>
                <div className="text-[11px] text-zinc-500 font-orbitron uppercase tracking-[0.12em]">
                  Use arrows to move cards / 使用上下按钮移动卡片
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 pb-2">
              {loading && submissions.length === 0 ? (
                <div className="text-zinc-500 text-center py-8 font-orbitron text-sm animate-pulse">LOADING...</div>
              ) : submissions.length === 0 ? (
                <div className="text-zinc-500 text-center py-8 font-orbitron text-sm">No submissions in this folder.</div>
              ) : (
                orderedSubmissions.map((sub, index) => {
                  const hasDoubt = hasSubmissionDoubt(sub);
                  const isSelected = selectedSubmissionIds.includes(sub.id);
                  const rank = submissionRanks[sub.id];
                  const currentJudgeScore = getNormalizedSubmissionJudgeScore(
                    sub,
                    (sub.judgeScores || []).find(score => score.judgeId === user.uid)
                  );
                  const finalTotalScore = getFinalSubmissionScore(sub);
                  const displayInitialScore = getDisplayedSubmissionInitialScore(sub, user.uid);
                  const displayTotalScore = currentJudgeScore?.totalScore ?? finalTotalScore;
                  const isFirstSubmission = index === 0;
                  const isLastSubmission = index === orderedSubmissions.length - 1;
                  return (
                    <div
                      key={sub.id}
                      onClick={() => setJudgingSubmissionId(sub.id)}
                      className={`rounded-xl p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 cursor-pointer transition-colors select-none ${
                        hasDoubt
                          ? 'bg-yellow-500/10 border border-yellow-400/60 hover:bg-yellow-500/15 hover:border-yellow-300'
                          : 'bg-black/60 border border-zinc-800 hover:border-blue-500/50 hover:bg-zinc-950/80'
                      }`}
                    >
                        <div>
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveSubmission(sub.id, -1);
                              }}
                              disabled={isFirstSubmission}
                              className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-orbitron uppercase tracking-[0.16em] transition-colors ${
                                isFirstSubmission
                                  ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                                  : 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200 hover:bg-cyan-400/20'
                              }`}
                            >
                              <ChevronUp size={12} /> 上移一位
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveSubmission(sub.id, 1);
                              }}
                              disabled={isLastSubmission}
                              className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-orbitron uppercase tracking-[0.16em] transition-colors ${
                                isLastSubmission
                                  ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500 cursor-not-allowed'
                                  : 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200 hover:bg-cyan-400/20'
                              }`}
                            >
                              <ChevronDown size={12} /> 下移一位
                            </button>
                          </div>
                          <div className="text-white font-rajdhani font-bold flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSubmissionSelection(sub.id);
                              }}
                              className={`h-5 w-5 rounded border flex items-center justify-center text-[11px] font-black transition-colors ${
                                isSelected
                                  ? 'bg-cyan-400/90 border-cyan-300 text-black'
                                  : 'bg-black/40 border-zinc-600 text-transparent hover:border-cyan-400'
                              }`}
                              aria-label={isSelected ? 'Deselect submission' : 'Select submission'}
                            >
                              ✓
                            </button>
                            {typeof rank === 'number' && (
                              <span className="px-3 py-1 rounded-full bg-gradient-to-r from-yellow-300/40 via-amber-300/35 to-yellow-200/40 border border-yellow-200/70 text-yellow-50 text-[11px] font-orbitron font-black uppercase tracking-[0.16em] shadow-[0_0_18px_rgba(253,224,71,0.28)]">
                                Rank #{rank} / 第{rank}名
                              </span>
                            )}
                            {sub.userDisplayName}
                            <span className="text-cyan-400 text-xs font-orbitron font-normal uppercase">
                              No. {getSubmissionCompetitionNumber(sub)}
                            </span>
                            <span className="text-zinc-500 text-xs font-mono font-normal">({sub.userId})</span>
                            {hasDoubt && (
                              <span className="px-2 py-0.5 rounded-full bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-[10px] font-orbitron uppercase tracking-wider">
                                Doubt / 存疑
                              </span>
                            )}
                          </div>
                          <div className="text-blue-400 text-xs font-orbitron mt-1">Deck: {sub.deckName}</div>
                          <div className="text-zinc-600 text-xs mt-1">{new Date(sub.createdAt).toLocaleString()}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <div className="px-2 py-1 rounded bg-cyan-400/10 border border-cyan-400/25 text-[10px] font-orbitron text-cyan-200">
                              Initial / 初始分: <span className="text-cyan-300">{formatScoreValue(displayInitialScore)}</span>
                            </div>
                            <div className={`px-2 py-1 rounded border text-[10px] font-orbitron ${
                              displayTotalScore === null
                                ? 'bg-white/5 border-white/10 text-zinc-500'
                                : 'bg-yellow-400/10 border-yellow-400/30 text-yellow-100'
                            }`}>
                              Total / 总分: <span className={displayTotalScore === null ? 'text-zinc-400' : 'text-yellow-400'}>
                                {displayTotalScore === null ? '--' : formatScoreValue(displayTotalScore)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:justify-end shrink-0">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setJudgingSubmissionId(sub.id);
                            }}
                            className="flex-1 min-w-[140px] lg:flex-none lg:min-w-0 justify-center flex items-center gap-1 px-3 py-1.5 bg-fuchsia-600/20 text-fuchsia-300 border border-fuchsia-500/30 rounded hover:bg-fuchsia-600/40 transition-colors text-xs font-orbitron uppercase tracking-wider"
                          >
                            <Gavel size={14} /> Judge / 评分
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              openPrintableA4Preview(sub);
                            }}
                            className="flex-1 min-w-[140px] lg:flex-none lg:min-w-0 justify-center flex items-center gap-1 px-3 py-1.5 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded hover:bg-blue-600/40 transition-colors text-xs font-orbitron uppercase tracking-wider"
                          >
                            <Download size={14} /> A4 图片
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSubmission(sub.id);
                            }}
                            className="p-1.5 bg-red-900/20 text-red-500 border border-red-500/30 rounded hover:bg-red-900/40 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-zinc-600 font-orbitron">
            Select a folder to view submissions.
          </div>
        )}

      {judgingSubmission && (
        (() => {
          const myJudgeScore = getJudgeScoreEntry(judgingSubmission);
          const draftStatuses = judgeDraftStatuses.length > 0
            ? [...judgeDraftStatuses]
            : [...myJudgeScore.trickStatuses];
          while (draftStatuses.length < judgingSubmission.tricks.length) draftStatuses.push('pending');
          const committedStatuses = [...myJudgeScore.trickStatuses];
          while (committedStatuses.length < judgingSubmission.tricks.length) committedStatuses.push('pending');
          const normalizedDraftInitialScore = judgeInitialScoreInput.trim() === '' ? 0 : Number(judgeInitialScoreInput);
          const myDraftInitialScore = Number.isFinite(normalizedDraftInitialScore)
            ? normalizedDraftInitialScore
            : (myJudgeScore.initialScore ?? 0);
          const committedInitialScore = myJudgeScore.initialScore ?? 0;
          const hasJudgeDraftChanges =
            isJudgeDraftDirty
            || myDraftInitialScore !== (myJudgeScore.initialScore ?? 0)
            || !areJudgeStatusesEqual(draftStatuses as SubmissionJudgeStatus[], myJudgeScore.trickStatuses);
          const committedScoreBreakdown = getJudgeScoreBreakdown(
            judgingSubmission.tricks,
            committedStatuses as SubmissionJudgeStatus[],
            committedInitialScore
          );
          const isSubmittingJudgeCard = savingJudgeKey === `${judgingSubmission.id}:submit-card`;
          if (judgeInitialScoreRenderDebugCountRef.current < 25) {
            judgeInitialScoreRenderDebugCountRef.current += 1;
            // #region debug-point H5:judge-panel-render
            reportDebugEvent('H5', 'judgePanel:render', {
              count: judgeInitialScoreRenderDebugCountRef.current,
              judgingSubmissionId: judgingSubmission.id,
              inputValue: judgeInitialScoreInput,
              myJudgeInitialScore: myJudgeScore.initialScore,
              myJudgeTotalScore: myJudgeScore.totalScore,
              breakdownInitialScore: committedScoreBreakdown.initialScore,
              savingJudgeKey
            }, judgeInitialScoreDebugSessionId);
            // #endregion
          }
          return (
            <div className="fixed inset-0 z-[70] flex items-start lg:items-center justify-center bg-black/90 p-2 pt-20 sm:p-4 sm:pt-24 overflow-y-auto">
              <div className="fixed top-20 right-3 z-[85] flex items-center gap-3 rounded-full border border-white/12 bg-black/45 px-3 py-2 shadow-[0_0_32px_rgba(6,8,22,0.55)] backdrop-blur-md sm:right-6 lg:top-24">
                <button
                  type="button"
                  onClick={handleRefreshJudgingPanel}
                  disabled={isRefreshingJudgingPanel}
                  title={isRefreshingJudgingPanel ? t('refreshing') : t('refreshScores')}
                  className={`inline-flex h-11 items-center justify-center gap-2 rounded-full border px-4 shadow-[0_0_18px_rgba(34,211,238,0.14)] transition-all ${
                    isRefreshingJudgingPanel
                      ? 'border-cyan-400/25 bg-cyan-400/16 text-cyan-100/55 cursor-not-allowed'
                      : 'border-cyan-300/45 bg-cyan-400/22 text-cyan-50 hover:border-cyan-200/70 hover:bg-cyan-300/30 hover:text-white hover:shadow-[0_0_24px_rgba(34,211,238,0.24)]'
                  }`}
                >
                  <RefreshCw size={18} className={isRefreshingJudgingPanel ? 'animate-spin' : ''} />
                  <span className="text-[11px] font-orbitron uppercase tracking-[0.16em]">
                    {isRefreshingJudgingPanel ? t('refreshing') : t('refreshScores')}
                  </span>
                </button>
                <button
                  onClick={() => setJudgingSubmissionId(null)}
                  title={t('close')}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/45 text-zinc-300 shadow-[0_0_18px_rgba(0,0,0,0.22)] transition-all hover:border-white/20 hover:bg-white/8 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="relative w-full max-w-6xl min-h-[calc(100vh-5.5rem)] overflow-y-auto rounded-3xl border border-fuchsia-500/30 bg-[#060816] text-white shadow-[0_0_60px_rgba(168,85,247,0.2)] lg:min-h-0 lg:max-h-[calc(100vh-6rem)] lg:overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] lg:h-full">
                  <div className="order-2 lg:order-1 p-4 sm:p-5 lg:p-6 lg:border-r border-white/10 lg:overflow-y-auto custom-scrollbar lg:max-h-[92vh]">
                    <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(260px,1fr)_minmax(320px,420px)] xl:items-start">
                      <div className="min-w-0">
                        <div className="text-xs font-orbitron uppercase tracking-[0.3em] text-fuchsia-300/70">Judge Panel / 裁判评分台</div>
                        <h2 className="mt-2 break-words text-3xl font-black font-rajdhani text-white">{judgingSubmission.userDisplayName}</h2>
                        <div className="mt-1 break-words text-sm text-zinc-400 font-orbitron">{judgingSubmission.deckName}</div>
                        <div className="mt-3 text-xs text-cyan-200/75 font-orbitron uppercase tracking-[0.16em]">
                          Use the floating buttons on the top-right to refresh or close. / 点击右上角悬浮按钮即可刷新分数或关闭
                        </div>
                      </div>
                      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:w-auto xl:min-w-[320px]">
                        <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/10 px-5 py-3 text-center">
                          <div className="text-[10px] font-orbitron uppercase tracking-[0.25em] text-yellow-300/70">Saved Score / 已提交得分</div>
                          <div className="mt-1 text-5xl font-black font-rajdhani text-yellow-400">{formatScoreValue(committedScoreBreakdown.totalScore)}</div>
                        </div>
                        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-3">
                          <div className="text-[10px] font-orbitron uppercase tracking-[0.25em] text-cyan-300/70">Initial Score / 初始分</div>
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="number"
                              step="0.1"
                              value={judgeInitialScoreInput}
                              onChange={(e) => setJudgeInitialScoreInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleJudgeScoreCardSubmit(judgingSubmission);
                                }
                              }}
                              className="flex-1 bg-black/40 border border-cyan-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400"
                              disabled={isSubmittingJudgeCard}
                            />
                          </div>
                          <div className="mt-2 text-[11px] text-cyan-200/70">
                            {hasJudgeDraftChanges
                              ? 'All trick judgments stay local until you submit all results below / 当前所有招式判定都只是本地草稿，需点击下方按钮统一提交'
                              : 'No unsaved changes / 当前没有未提交改动'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-5 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <div className="text-[10px] font-orbitron uppercase tracking-[0.24em] text-cyan-300/70">Submit All Judgments / 提交全部判定</div>
                          <div className="mt-1 text-sm text-zinc-300">
                            所有招式的成功、失败、存疑和初始分都会通过这一个按钮一次性提交。
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.preventDefault();
                            void handleJudgeScoreCardSubmit(judgingSubmission);
                          }}
                          disabled={isSubmittingJudgeCard || !hasJudgeDraftChanges}
                          className={`inline-flex min-w-[240px] items-center justify-center rounded-xl border px-5 py-3 text-sm font-orbitron uppercase tracking-[0.18em] transition-colors ${
                            isSubmittingJudgeCard || !hasJudgeDraftChanges
                              ? 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300/50 cursor-not-allowed'
                              : 'border-cyan-300/45 bg-cyan-400/20 text-cyan-50 hover:border-cyan-200/70 hover:bg-cyan-300/30'
                          }`}
                        >
                          {isSubmittingJudgeCard ? 'Submitting All / 提交全部中' : 'Submit All Judgments / 提交全部判定'}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
                      <div className="rounded-xl border border-cyan-300/45 bg-cyan-300/18 shadow-[0_0_24px_rgba(103,232,249,0.16)] px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-cyan-100">Initial / 初始分</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-cyan-50">{formatScoreValue(committedScoreBreakdown.initialScore)}</div>
                      </div>
                      <div className="rounded-xl border border-violet-300/45 bg-violet-300/18 shadow-[0_0_24px_rgba(196,181,253,0.16)] px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-violet-100">Square / 乘算分</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-violet-50">{formatScoreValue(committedScoreBreakdown.baseSum)}</div>
                      </div>
                      <div className="rounded-xl border border-fuchsia-300/45 bg-fuchsia-300/18 shadow-[0_0_24px_rgba(240,171,252,0.16)] px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-fuchsia-100">Bonus / 奖励分</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-fuchsia-50">{formatScoreValue(committedScoreBreakdown.bonus)}</div>
                      </div>
                      <div className="rounded-xl border border-yellow-300/50 bg-yellow-300/18 shadow-[0_0_24px_rgba(253,224,71,0.18)] px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-yellow-100">All Success / 全成追加</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-yellow-50">{formatScoreValue(committedScoreBreakdown.allSuccessBonus)}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                      <div className="rounded-xl border border-green-500/20 bg-green-500/10 px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-green-300/70">Success / 成功</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-green-400">{getStatusCount(committedStatuses, 'success')}</div>
                      </div>
                      <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-red-300/70">Fail / 失败</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-red-400">{getStatusCount(committedStatuses, 'loss')}</div>
                      </div>
                      <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-amber-300/70">Doubt / 存疑</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-amber-400">{getStatusCount(committedStatuses, 'doubt')}</div>
                      </div>
                      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-3">
                        <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-cyan-300/70">Pending / 未判</div>
                        <div className="mt-1 text-2xl font-black font-rajdhani text-cyan-400">{getStatusCount(committedStatuses, 'pending')}</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {judgingSubmission.tricks.map((trick, index) => {
                        const committedStatus = committedStatuses[index] || 'pending';
                        const draftStatus = draftStatuses[index] || 'pending';
                        const hasPendingStatusChange = draftStatus !== committedStatus;
                        const trickScore = parseSubmissionTrickScore(trick);
                        return (
                          <div key={`${judgingSubmission.id}-${index}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="h-8 min-w-8 rounded-full bg-yellow-400 text-black flex items-center justify-center font-black text-sm">{index + 1}</div>
                                  <div className="text-yellow-300 font-orbitron text-xs uppercase tracking-[0.24em]">{trickScore} pts base</div>
                                </div>
                                <div className="text-lg md:text-xl font-rajdhani font-bold text-white whitespace-pre-wrap break-words leading-tight">{trick}</div>
                              </div>
                              <div className="grid grid-cols-3 gap-2 shrink-0 md:w-[290px]">
                                {([
                                  {
                                    key: 'success',
                                    label: '成功',
                                    english: 'Success',
                                    icon: CheckCircle2,
                                    activeClass: 'bg-green-600 text-white border-green-400',
                                    idleClass: 'bg-zinc-900/80 text-zinc-400 border-zinc-700 hover:border-green-500 hover:text-green-400',
                                    pendingDraftClass: 'border-green-300/75 bg-green-500/12 text-green-100 shadow-[inset_0_0_0_1px_rgba(134,239,172,0.4)]'
                                  },
                                  {
                                    key: 'loss',
                                    label: '失败',
                                    english: 'Fail',
                                    icon: XCircle,
                                    activeClass: 'bg-red-600 text-white border-red-400',
                                    idleClass: 'bg-zinc-900/80 text-zinc-400 border-zinc-700 hover:border-red-500 hover:text-red-400',
                                    pendingDraftClass: 'border-red-300/75 bg-red-500/12 text-red-100 shadow-[inset_0_0_0_1px_rgba(252,165,165,0.4)]'
                                  },
                                  {
                                    key: 'doubt',
                                    label: '存疑',
                                    english: 'Doubt',
                                    icon: AlertCircle,
                                    activeClass: 'bg-amber-500 text-black border-amber-300',
                                    idleClass: 'bg-zinc-900/80 text-zinc-400 border-zinc-700 hover:border-amber-500 hover:text-amber-400',
                                    pendingDraftClass: 'border-amber-300/80 bg-amber-500/14 text-amber-100 shadow-[inset_0_0_0_1px_rgba(252,211,77,0.45)]'
                                  }
                                ] as const).map(option => {
                                  const Icon = option.icon;
                                  const isCommittedActive = committedStatus === option.key;
                                  const isDraftActive = draftStatus === option.key;
                                  const nextStatus = isDraftActive ? 'pending' : option.key;
                                  const pendingDraftClass = isDraftActive && hasPendingStatusChange
                                    ? option.pendingDraftClass
                                    : '';
                                  const buttonClass = hasPendingStatusChange
                                    ? `${pendingDraftClass || option.idleClass}`
                                    : (isCommittedActive ? option.activeClass : option.idleClass);
                                  return (
                                    <button
                                      key={option.key}
                                      type="button"
                                      onClick={() => handleJudgeStatusUpdate(judgingSubmission, index, nextStatus)}
                                      disabled={isSubmittingJudgeCard}
                                      className={`border rounded-xl px-2 py-2 flex flex-col items-center justify-center gap-1 transition-all ${buttonClass} ${isSubmittingJudgeCard ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    >
                                      <Icon size={16} />
                                      <span className="text-[9px] font-orbitron uppercase tracking-widest">{option.english}</span>
                                      <span className="text-[11px] font-bold">{option.label}</span>
                                      {isDraftActive && hasPendingStatusChange ? (
                                        <span className="text-[9px] font-orbitron uppercase tracking-[0.16em]">待提交</span>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            {hasPendingStatusChange ? (
                              <div className="mt-3 text-[11px] font-orbitron uppercase tracking-[0.16em] text-cyan-300/80">
                                Pending draft change / 当前判定尚未提交
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="order-1 lg:order-2 p-4 sm:p-5 lg:p-6 lg:overflow-y-auto custom-scrollbar lg:max-h-[92vh] bg-white/[0.02] border-b lg:border-b-0 lg:border-l-0 border-white/10">
                    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 px-4 py-4 mb-4">
                      <div className="text-[10px] font-orbitron uppercase tracking-[0.24em] text-cyan-300/70">My Judge Score / 我的评分</div>
                      <div className="mt-2 text-2xl sm:text-3xl font-black font-rajdhani text-cyan-300 break-words">{myJudgeScore.judgeName}</div>
                      <div className="mt-1 text-sm text-zinc-400">
                        {hasJudgeDraftChanges
                          ? 'Draft only until submitted / 提交前仅本地草稿'
                          : 'Saved score card / 当前评分卡已保存'}
                      </div>
                    </div>

                    <h3 className="text-sm font-orbitron uppercase tracking-[0.24em] text-zinc-400 mb-3">All Judges / 全部裁判分数</h3>
                    <div className="mb-3 text-[11px] text-zinc-500 font-orbitron">
                      Select one judge score card as the final score for ranking. / 勾选一张裁判打分卡作为最终分用于排名。
                    </div>
                    <div className="space-y-3">
                      {(judgingSubmission.judgeScores || [])
                        .map((score, index) => {
                          const normalizedScore = getNormalizedSubmissionJudgeScore(judgingSubmission, score) || score;
                          const breakdown = getJudgeScoreBreakdown(
                            judgingSubmission.tricks,
                            normalizedScore.trickStatuses as SubmissionJudgeStatus[],
                            typeof normalizedScore.initialScore === 'number' ? normalizedScore.initialScore : 0
                          );
                          const isFinalScoreCard = index === 0;
                          const isUpdatingJudgeOrder = savingJudgeKey === `${judgingSubmission.id}:judge-order`;
                          return (
                          <div
                            key={normalizedScore.judgeId}
                            className={`rounded-2xl border bg-white/[0.04] p-4 transition-colors ${
                              isFinalScoreCard
                                ? 'border-yellow-400/35 shadow-[0_0_20px_rgba(250,204,21,0.08)]'
                                : 'border-white/10'
                            } ${isUpdatingJudgeOrder ? 'opacity-70' : ''}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-start gap-3">
                                <div>
                                  {isFinalScoreCard && (
                                    <div className="mb-1 text-[10px] font-orbitron uppercase tracking-[0.22em] text-yellow-300/80">
                                      Final Score / 最终分
                                    </div>
                                  )}
                                  <div className="text-lg font-bold font-rajdhani text-white">{normalizedScore.judgeName}</div>
                                  <div className="text-[11px] text-zinc-500 font-orbitron uppercase tracking-[0.22em]">
                                    {normalizedScore.updatedAt ? new Date(normalizedScore.updatedAt).toLocaleString() : 'No updates'}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <div className="text-[10px] font-orbitron uppercase tracking-[0.22em] text-zinc-500">Total</div>
                                  <div className="text-3xl font-black font-rajdhani text-yellow-400">{formatScoreValue(normalizedScore.totalScore)}</div>
                                  <div className="text-[11px] text-cyan-300/80 font-orbitron mt-1">Initial {formatScoreValue(breakdown.initialScore)}</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleSelectFinalJudgeScore(judgingSubmission, normalizedScore.judgeId)}
                                  disabled={isUpdatingJudgeOrder || isFinalScoreCard}
                                  className={`inline-flex min-w-[112px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-orbitron uppercase tracking-[0.18em] transition-colors ${
                                    isFinalScoreCard
                                      ? 'border-yellow-400/35 bg-yellow-400/10 text-yellow-300'
                                      : 'border-cyan-400/20 bg-cyan-500/10 text-cyan-200 hover:border-cyan-300/40 hover:bg-cyan-500/15'
                                  } ${isUpdatingJudgeOrder ? 'cursor-not-allowed opacity-60' : ''}`}
                                >
                                  <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${isFinalScoreCard ? 'border-yellow-300 bg-yellow-300 text-black' : 'border-cyan-300/50 text-transparent'}`}>
                                    <Check size={11} />
                                  </span>
                                  {isFinalScoreCard ? '最终分' : '设为最终分'}
                                </button>
                              </div>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                              <div className="rounded-lg bg-green-500/10 border border-green-500/15 px-2 py-2">
                                <div className="text-[10px] font-orbitron text-green-300/70 uppercase">成功</div>
                                <div className="text-lg font-black font-rajdhani text-green-400">{getStatusCount(normalizedScore.trickStatuses as SubmissionJudgeStatus[], 'success')}</div>
                              </div>
                              <div className="rounded-lg bg-red-500/10 border border-red-500/15 px-2 py-2">
                                <div className="text-[10px] font-orbitron text-red-300/70 uppercase">失败</div>
                                <div className="text-lg font-black font-rajdhani text-red-400">{getStatusCount(normalizedScore.trickStatuses as SubmissionJudgeStatus[], 'loss')}</div>
                              </div>
                              <div className="rounded-lg bg-amber-500/10 border border-amber-500/15 px-2 py-2">
                                <div className="text-[10px] font-orbitron text-amber-300/70 uppercase">存疑</div>
                                <div className="text-lg font-black font-rajdhani text-amber-400">{getStatusCount(normalizedScore.trickStatuses as SubmissionJudgeStatus[], 'doubt')}</div>
                              </div>
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                              <div className="rounded-lg bg-violet-500/10 border border-violet-500/15 px-2 py-2">
                                <div className="text-[10px] font-orbitron text-violet-300/70 uppercase">乘算</div>
                                <div className="text-lg font-black font-rajdhani text-violet-300">{formatScoreValue(breakdown.baseSum)}</div>
                              </div>
                              <div className="rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/15 px-2 py-2">
                                <div className="text-[10px] font-orbitron text-fuchsia-300/70 uppercase">奖励</div>
                                <div className="text-lg font-black font-rajdhani text-fuchsia-300">{formatScoreValue(breakdown.bonus)}</div>
                              </div>
                              <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/15 px-2 py-2">
                                <div className="text-[10px] font-orbitron text-yellow-300/70 uppercase">全成</div>
                                <div className="text-lg font-black font-rajdhani text-yellow-300">{formatScoreValue(breakdown.allSuccessBonus)}</div>
                              </div>
                            </div>
                          </div>
                        )})}
                      {(!judgingSubmission.judgeScores || judgingSubmission.judgeScores.length === 0) && (
                        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-zinc-500 font-orbitron text-sm">
                          No judge scores yet / 暂无裁判评分
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      )}
      </div>

      {/* Hidden Export Template */}
      {viewingSubmission && (
        (() => {
          const exportPages = getSubmissionA4Pages(viewingSubmission);
          const previewScale = typeof window !== 'undefined'
            ? Math.min(1, Math.max(0.34, (window.innerWidth - 40) / A4_EXPORT_WIDTH))
            : 1;
          return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-2 sm:p-4">
           <div className="relative w-full max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)] max-h-[92vh] overflow-y-auto custom-scrollbar rounded-3xl border border-zinc-700 bg-[#111111] p-3 sm:p-4 text-white shadow-[0_0_60px_rgba(0,0,0,0.35)]">
             <button onClick={() => setViewingSubmission(null)} className="absolute top-3 right-3 text-zinc-400 hover:text-white bg-black/40 border border-white/10 rounded-full p-1.5 shadow-md z-10">
               <X size={24} />
             </button>
             
             <div className="mb-3 text-center text-[11px] sm:text-xs font-orbitron uppercase tracking-[0.22em] text-zinc-300">
               A4 Preview / A4 预览
             </div>

             <div className="overflow-auto max-h-[calc(92vh-7rem)] custom-scrollbar rounded-2xl border border-white/8 bg-black/25 p-2 sm:p-3">
               <div className="mx-auto flex flex-col items-center gap-4">
                 {exportPages.map((pageTricks, pageIndex) => (
                   <div
                     key={`${viewingSubmission.id}-preview-page-${pageIndex + 1}`}
                     className="origin-top"
                     style={{
                       width: `${A4_EXPORT_WIDTH * previewScale}px`,
                       height: `${A4_EXPORT_HEIGHT * previewScale}px`
                     }}
                   >
                     <div
                       style={{
                         transform: `scale(${previewScale})`,
                         transformOrigin: 'top center',
                         width: A4_EXPORT_WIDTH,
                         height: A4_EXPORT_HEIGHT
                       }}
                     >
                       {renderPrintableA4Page(viewingSubmission, pageTricks, pageIndex)}
                     </div>
                   </div>
                 ))}
               </div>
             </div>

             <div className="mt-3 text-center text-[11px] text-zinc-400">
               每页最多 10 个招式，超过后自动拆到下一张 A4 图片
             </div>
             
             <div className="mt-4 flex justify-center">
                <button 
                  onClick={() => handleExportA4JPG(viewingSubmission)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white text-black rounded-xl hover:bg-zinc-200 font-orbitron font-bold tracking-wider"
                >
                  <Download size={20} /> {exportPages.length > 1 ? 'SAVE A4 JPGS / 保存多张 A4 图片' : 'SAVE A4 JPG / 保存 A4 图片'}
                </button>
             </div>
           </div>
        </div>
          );
        })()
      )}
    </div>
  );
};

export default AdminSubmissions;
